import {Component, OnDestroy, OnInit} from '@angular/core';
import {AccountService, AlertService, GroupService, UserService} from "../services";
import { Subject } from 'rxjs';
import {User} from "../models";
import {ActivatedRoute} from "@angular/router";
import {first, takeUntil} from "rxjs/operators";
import {ServiceService} from "../services/service.service";
import {ComponentTitleService} from "../services/componentTitle.service";

import { locale as polish } from './i18n/pl';
import { locale as english } from './i18n/en';
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";
import {Notes} from "../note/model/notes";
import {NoteService} from "../note/service/note.service";
import {Note} from "../note/model/note";
import {Card} from "../models/card";
import {CardService} from "../services/card.service";
import {VoucherTemplate} from "../models/voucherTemplate";
import {VoucherTemplateService} from "../services/voucherTemplate.service";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {File} from "../models/file";
import {InternalFileService} from "../internalFile/service/internalFile.service";
import {Training} from "../models/training";
import {TrainingService} from "../services/training.service";
import {TrainingItems} from "../models/trainingItems";

@Component({
    templateUrl: './customerDetails.component.html',
    styleUrls: ['./customerDetails.component.scss']
})
export class CustomerDetailsComponent implements OnDestroy, OnInit {
    id: string;
    user: User;
    notes: Note[];
    cards: Card[] = [];
    vouchers: VoucherTemplate[] = [];
    medicoverVouchers: VoucherTemplate[] = [];
    profitVouchers: VoucherTemplate[] = [];
    pzuSportVouchers: VoucherTemplate[] = [];
    form: FormGroup;
    medicoverForm: FormGroup;
    submitted: boolean = false;
    loading: boolean = false;
    multiSportUsedToday: boolean = false;
    multiSportError: boolean = false;
    multiSportMessage: string;
    medicoverUsedToday: boolean = false;
    medicoverError: boolean = false;
    medicoverMessage: string;
    profitUsedToday: boolean = false;
    profitError: boolean = false;
    profitMessage: string;
    profitForm: FormGroup;
    pzuSportUsedToday: boolean = false;
    pzuSportError: boolean = false;
    pzuSportMessage: string;
    pzuSportForm: FormGroup;
    cardExists = false;
    cardNumber: string = '';
    checkingCardNumber = false;
    trainings: Training[] = [];
    trainingsLoading: boolean = false;

    private _unsubscribeAll: Subject<any>;

    constructor(
        private formBuilder: FormBuilder,
        private userService: UserService,
        private alertService: AlertService,
        private accountService: AccountService,
        private route: ActivatedRoute,
        private serviceService: ServiceService,
        private componentTitleService: ComponentTitleService,
        private translationLoader: FuseTranslationLoaderService,
        private noteService: NoteService,
        private cardService: CardService,
        private voucherTemplateService: VoucherTemplateService,
        private internalFileService: InternalFileService,
        private trainingService: TrainingService,
    ) {
        this._unsubscribeAll = new Subject();

        componentTitleService.clearTitle();
        componentTitleService.addPart('Customers');

        this.translationLoader.loadTranslations(polish, english);
    }

    ngOnInit() {
        this.id = this.route.snapshot.params['id'];

        this.form = this.formBuilder.group({
            userId: [this.id, Validators.required],
            cardNumber: ['', Validators.required],
            cardUuid: [''],
            multiSportVoucherId: ['', Validators.required],
        });

        this.medicoverForm = this.formBuilder.group({
            userId: [this.id, Validators.required],
            cardNumber: ['', Validators.required],
            cardUuid: [''],
            medicoverVoucherId: ['', Validators.required],
        });

        this.profitForm = this.formBuilder.group({
            userId: [this.id, Validators.required],
            cardNumber: ['', Validators.required],
            cardUuid: [''],
            profitVoucherId: ['', Validators.required],
        });

        this.pzuSportForm = this.formBuilder.group({
            userId: [this.id, Validators.required],
            cardNumber: ['', Validators.required],
            cardUuid: [''],
            pzuSportVoucherId: ['', Validators.required],
        });

        this.user = this.route.snapshot.data.userData;
        let multiSportDate = new Date(this.user.lastMultiSportUse);
        let today = new Date();

        if (
            multiSportDate.getDate() === today.getDate() &&
            multiSportDate.getMonth() === today.getMonth() &&
            multiSportDate.getFullYear() === today.getFullYear()
        ) {
            this.multiSportUsedToday = true;
        }

        let medicoverDate = new Date(this.user.lastMedicoverUse);

        if (
            medicoverDate.getDate() === today.getDate() &&
            medicoverDate.getMonth() === today.getMonth() &&
            medicoverDate.getFullYear() === today.getFullYear()
        ) {
            if (this.user.medicoverPlus && medicoverDate.getHours()+1 <= today.getHours()) {
                this.medicoverUsedToday = false;
            } else {
                this.medicoverUsedToday = true;
            }
        }

        let profitDate = new Date(this.user.lastProfitUse);

        if (
            profitDate.getDate() === today.getDate() &&
            profitDate.getMonth() === today.getMonth() &&
            profitDate.getFullYear() === today.getFullYear()
        ) {
            this.profitUsedToday = true;
        }

        let pzuSportDate = new Date(this.user.lastPzuSportUse);

        if (
            pzuSportDate.getDate() === today.getDate() &&
            pzuSportDate.getMonth() === today.getMonth() &&
            pzuSportDate.getFullYear() === today.getFullYear()
        ) {
            this.pzuSportUsedToday = true;
        }

        this.componentTitleService.addPart(this.user.firstName + ' ' + this.user.lastName);

        this.noteService.getAll(0, 100, this.user.id, null).pipe(first())
            .subscribe((res: Notes) => {
                    this.notes = res.items;
                    // this.page = pagedData.page;
                },
                error => {
                    this.alertService.error(error);
                });

        if (this.user.multiSport) {
            this.cardService.findCustomerCards(this.id)
                .pipe(first())
                .subscribe((customerCards: Card[]) => {
                    this.cards = customerCards;

                    if (this.cards.length>0) {
                        this.f.cardUuid.setValue(this.cards[0].uuid);
                        this.f.cardNumber.setValue(this.cards[0].cardNumber);
                        this.cardNumber = this.cards[0].cardNumber;
                        if (this.cards[0].cardNumber === 'Karta zwrócona' || this.cards[0].cardNumber === 'Card returned') {
                            this.f.cardNumber.setValue('');
                            this.cardNumber = '';
                        }
                    }
                });

            this.voucherTemplateService.getMultiSportVouchers().pipe(first())
                .subscribe( (res: VoucherTemplate[])  => {
                        this.vouchers = res;
                    },
                    error => {
                        this.alertService.error(error);
                    });
        }

        if (this.user.medicover) {
            this.cardService.findCustomerCards(this.id)
                .pipe(first())
                .subscribe((customerCards: Card[]) => {
                    this.cards = customerCards;

                    if (this.cards.length>0) {
                        this.medicoverF.cardUuid.setValue(this.cards[0].uuid);
                        this.medicoverF.cardNumber.setValue(this.cards[0].cardNumber);
                        this.cardNumber = this.cards[0].cardNumber;
                        if (this.cards[0].cardNumber === 'Karta zwrócona' || this.cards[0].cardNumber === 'Card returned') {
                            this.f.cardNumber.setValue('');
                            this.cardNumber = '';
                        }
                    }
                });

            this.voucherTemplateService.getMedicoverVouchers().pipe(first())
                .subscribe( (res: VoucherTemplate[])  => {
                        this.medicoverVouchers = res;
                    },
                    error => {
                        this.alertService.error(error);
                    });
        }

        if (this.user.profit) {
            this.cardService.findCustomerCards(this.id)
                .pipe(first())
                .subscribe((customerCards: Card[]) => {
                    this.cards = customerCards;

                    if (this.cards.length>0) {
                        this.profitF.cardUuid.setValue(this.cards[0].uuid);
                        this.profitF.cardNumber.setValue(this.cards[0].cardNumber);

                        this.cardNumber = this.cards[0].cardNumber;
                        if (this.cards[0].cardNumber === 'Karta zwrócona' || this.cards[0].cardNumber === 'Card returned') {
                            this.f.cardNumber.setValue('');
                            this.cardNumber = '';
                        }
                    }
                });

            this.voucherTemplateService.getProfitVouchers().pipe(first())
                .subscribe( (res: VoucherTemplate[])  => {
                        this.profitVouchers = res;
                    },
                    error => {
                        this.alertService.error(error);
                    });
        }

        if (this.user.pzuSport) {
            this.cardService.findCustomerCards(this.id)
                .pipe(first())
                .subscribe((customerCards: Card[]) => {
                    this.cards = customerCards;

                    if (this.cards.length>0) {
                        this.pzuSportF.cardUuid.setValue(this.cards[0].uuid);
                        this.pzuSportF.cardNumber.setValue(this.cards[0].cardNumber);
                        this.cardNumber = this.cards[0].cardNumber;
                        if (this.cards[0].cardNumber === 'Karta zwrócona' || this.cards[0].cardNumber === 'Card returned') {
                            this.f.cardNumber.setValue('');
                            this.cardNumber = '';
                        }
                    }
                });

            this.voucherTemplateService.getPzuSportVouchers().pipe(first())
                .subscribe( (res: VoucherTemplate[])  => {
                        this.pzuSportVouchers = res;
                    },
                    error => {
                        this.alertService.error(error);
                    });
        }

        // Load customer trainings
        this.loadTrainings();
    }

    loadTrainings(): void {
        this.trainingsLoading = true;
        // Get user card number for filtering
        const userId = this.user.id;

        if (userId) {
            this.trainingService.getTrainings({
                userId: userId,
                limit: 50,
                sortCol: 'startedAt',
                sortDir: 'desc'
            }).pipe(first()).subscribe(
                (res: TrainingItems) => {
                    this.trainings = res.items;
                    this.trainingsLoading = false;
                },
                error => {
                    this.alertService.error(error);
                    this.trainingsLoading = false;
                }
            );
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    onSubmit(): void {
        this.submitted = true;

        // reset alerts on submit
        this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;
        let data = this.form.value;

        this.cardService.addMultiSportVoucher(data.cardNumber, data.cardUuid, data.multiSportVoucherId, data.userId).pipe(first())
            .subscribe(res => {
                window.location.reload();
            },
            err => {
                this.multiSportError = true;
                this.multiSportMessage = err;
            });
    }

    onSubmitMedicover(): void {
        this.submitted = true;

        // reset alerts on submit
        this.alertService.clear();

        // stop here if form is invalid
        if (this.medicoverForm.invalid) {
            return;
        }

        this.loading = true;
        let data = this.medicoverForm.value;

        this.cardService.addMedicoverVoucher(data.cardNumber, data.cardUuid, data.medicoverVoucherId, data.userId).pipe(first())
            .subscribe(res => {
                    window.location.reload();
                },
                err => {
                    this.medicoverError = true;
                    this.medicoverMessage = err;
                });
    }

    onSubmitPzuSport(): void {
        this.submitted = true;

        // reset alerts on submit
        this.alertService.clear();

        // stop here if form is invalid
        if (this.pzuSportForm.invalid) {
            return;
        }

        this.loading = true;
        let data = this.pzuSportForm.value;

        this.cardService.addPzuSportVoucher(data.cardNumber, data.cardUuid, data.pzuSportVoucherId, data.userId).pipe(first())
            .subscribe(res => {
                    window.location.reload();
                },
                err => {
                    this.pzuSportError = true;
                    this.pzuSportMessage = err;
                });
    }

    onSubmitProfit(): void {
        this.submitted = true;

        // reset alerts on submit
        this.alertService.clear();

        // stop here if form is invalid
        if (this.profitForm.invalid) {
            return;
        }

        this.loading = true;
        let data = this.profitForm.value;

        this.cardService.addProfitVoucher(data.cardNumber, data.cardUuid, data.profitVoucherId, data.userId).pipe(first())
            .subscribe(res => {
                    window.location.reload();
                },
                err => {
                    this.profitError = true;
                    this.profitMessage = err;
                });
    }

    selectMultiSportVoucher(voucher: VoucherTemplate): void
    {
        this.f.multiSportVoucherId.setValue(voucher.id);
    }

    selectMedicoverVoucher(voucher: VoucherTemplate): void
    {
        this.medicoverF.medicoverVoucherId.setValue(voucher.id);
    }

    selectPzuSportVoucher(voucher: VoucherTemplate): void
    {
        this.pzuSportF.pzuSportVoucherId.setValue(voucher.id);
    }

    selectProfitVoucher(voucher: VoucherTemplate): void
    {
        this.profitF.profitVoucherId.setValue(voucher.id);
    }

    get f() { return this.form.controls; }
    get medicoverF() { return this.medicoverForm.controls; }
    get pzuSportF() { return this.pzuSportForm.controls; }
    get profitF() { return this.profitForm.controls; }

    verifyCardNumber(event: any) {
        this.unsubscribe();
        this.cardNumber = event.target.value;
        this.checkingCardNumber = true;
        this.cardExists = true;
        this.cardService.getByNumber(event.target.value).pipe(takeUntil(this._unsubscribeAll))
            .subscribe(data => {
                if (data.userId !== this.user.id) {
                    this.cardExists = true;
                } else {
                    this.cardExists = false;
                }
                this.checkingCardNumber = false;
            }, error => {
                this.cardExists = false;
                this.checkingCardNumber = false;
            });
    }

    private unsubscribe(): void {
        this._unsubscribeAll.next();
    }

    downloadFile(document: File) {
        document.downloading = true;
        this.internalFileService.downloadFile(document.id).subscribe(res => {
            var byteCharacters = atob(res);
            var byteNumbers = new Array(byteCharacters.length);
            for (var i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            var byteArray = new Uint8Array(byteNumbers);
            var file = new Blob([byteArray], { type: document.mimeType + ';base64' });
            var fileURL = URL.createObjectURL(file);
            window.open(fileURL);
            document.downloading = false;
        }, err => {
            document.downloading = false;
        });
    }
}
